import { forwardRef, Ref } from 'react'
import * as Select from '@radix-ui/react-select'
import { ChevronDownIcon, XIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

interface CustomSelectProps {
  options: { value: string; text: string }[]
  placeholder?: string
  value?: string
  // eslint-disable-next-line no-unused-vars
  onChange?: (value: string) => void
  onReset?: () => void
  error?: boolean
  disabled?: boolean
  resetKey?: string | number
}

const CustomSelect = forwardRef(
  (
    {
      options,
      placeholder,
      value,
      onChange,
      onReset,
      error,
      disabled,
      resetKey
    }: CustomSelectProps,
    ref: Ref<HTMLButtonElement>
  ) => {
    const handleValueChange = (newValue: string) => {
      onChange?.(newValue)
    }

    const handleReset = (e: React.MouseEvent) => {
      e.stopPropagation()
      onReset?.()
    }

    return (
      <Select.Root
        key={resetKey}
        value={value}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <Select.Trigger
          ref={ref}
          className={cn(
            'flex w-full items-center justify-between rounded-[0.4rem] px-spacing-xs py-[0.9rem] font-roboto text-regular',
            'border border-text-primary-dark/20 text-text-primary-dark',
            'focus:border-text-primary-dark/80 focus:ring-2 focus:ring-text-primary-dark/30',
            {
              'border-error focus:ring-error/50': error,
              'pointer-events-none opacity-40': disabled
            }
          )}
        >
          <Select.Value placeholder={placeholder} />
          <div className="flex items-center space-x-2">
            {value && onReset && (
              <button
                type="button"
                onClick={handleReset}
                className="text-text-primary-dark/50 hover:text-text-primary-dark"
              >
                <XIcon size={16} />
              </button>
            )}
            <Select.Icon>
              <ChevronDownIcon />
            </Select.Icon>
          </div>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            position="popper"
            className={cn(
              'z-10 w-full rounded-[0.4rem] bg-text-button-primary-dark',
              'border border-text-primary-dark/20',
              'w-[var(--radix-select-trigger-width)]'
            )}
          >
            <Select.Viewport className="p-1">
              {options.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  className={cn(
                    'cursor-pointer px-4 py-2 font-roboto text-text-primary-dark',
                    'data-[highlighted]:bg-text-primary-dark/10 data-[state=checked]:bg-text-primary-dark/20'
                  )}
                >
                  <Select.ItemText>{option.text}</Select.ItemText>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    )
  }
)

CustomSelect.displayName = 'CustomSelect'

export default CustomSelect
